import { PayloadAction } from '@reduxjs/toolkit';
import { NetworkQualityScore } from '@yandex-video-platform/goloom-sdk';
import { call, delay, put, select } from 'typed-redux-saga';

import { selectIsTestplane } from '../../../../shared/features/flags/selectors';
import {
    DEFAULT_TIMEOUT,
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import {
    getShowMyVideo,
    selectHideParticipantsVideo,
    selectWasParticipantsVideoNotificationShown,
    setWasParticipantsVideoNotificationShownAction,
} from '../../../../shared/features/settings';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import Storage from '../../../../shared/helpers/storage';
import { NetworkQualityType } from '../../constants';
import { getNetworkQualityTypeByScore } from '../../helpers/get-network-quality-type-by-score';
import { selectBadNetworkQualityNotificationWasShown, selectNetworkQualityType } from '../../selectors';
import { NotificationId } from '../../types';
import { setBadNetworkQualityNotificationWasShown, setNetworkQualityType } from './slice';

const logger = createLogger('network-quality:sagas');

export interface NetworkQualityChangedEvent {
    value: NetworkQualityScore;
    prev: NetworkQualityScore;
}

export function* onNetworkQualityChangedSaga({ value, prev }: NetworkQualityChangedEvent) {
    if (yield* select(selectIsTestplane)) {
        return;
    }

    const isCurrentNetworkQualityType = getNetworkQualityTypeByScore(value);

    yield* put(setNetworkQualityType(isCurrentNetworkQualityType));

    const isPrevNetworkQualityType = getNetworkQualityTypeByScore(prev);

    // Если новое и прошлое значение одного типа, то не показываем нотификаций
    if (isCurrentNetworkQualityType === isPrevNetworkQualityType) {
        return;
    }

    logger.event('Network quality changed', {
        additional: {
            method: 'onNetworkQualityChangedSaga',
            prev,
            value,
        },
    });
    const showMyVideo = yield* select(getShowMyVideo);
    const isMobile = yield* select(isMobileUa);
    const participantsVideoDisabled = yield* select(selectHideParticipantsVideo);
    const wasParticipantsVideoNotificationShown = yield* select(selectWasParticipantsVideoNotificationShown);

    // Если состояние изменилось на хорошее,
    // и пользователь отключал видео участников
    // и мы ему еще не показали тост с предложением включить видео
    if (
        isCurrentNetworkQualityType === NetworkQualityType.GOOD &&
        participantsVideoDisabled &&
        !wasParticipantsVideoNotificationShown
    ) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_SHOW_VIDEO](isMobile)));
        yield* put(setWasParticipantsVideoNotificationShownAction(true));
        yield* call([Storage, 'setItem'], 'wasParticipantsVideoNotificationShown', JSON.stringify(true));
        return;
    }

    // Если состояние изменилось на хорошее
    if (isCurrentNetworkQualityType === NetworkQualityType.GOOD) {
        yield* put(hideNotification(NotificationId.NETWORK_QUALITY_BAD));
        return;
    }

    // Если мы тут, то состояние изменилось на плохое

    // Если пользователь уже выключил видео участников
    if (participantsVideoDisabled) {
        // Если пользователь скрыл свое видео, то нотификация должна отображаться до улучшения связи
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_BAD]({ infinite: !showMyVideo })));
        return;
    }

    const badNetworkQualityNotificationWasShown = yield* select(selectBadNetworkQualityNotificationWasShown);
    if (badNetworkQualityNotificationWasShown) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_BAD]({ infinite: true })));
        return;
    }

    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_HIDE_VIDEO](isMobile)));
    yield* put(setBadNetworkQualityNotificationWasShown(true));
    // Если пользователь видит свое видео
    if (showMyVideo) {
        return;
    }

    // Если пользователь не видит свое видео
    yield* delay(DEFAULT_TIMEOUT);
    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_BAD]({ infinite: true })));
}

export function* onToggleShowMyVideo({ payload }: PayloadAction<boolean>) {
    if (payload) {
        yield* put(hideNotification(NotificationId.NETWORK_QUALITY_BAD));
        return;
    }

    const networkQualityType = yield* select(selectNetworkQualityType);

    if (networkQualityType === NetworkQualityType.BAD) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.NETWORK_QUALITY_BAD]({ infinite: true })));
    }
}
